import {
  type UniversalHelpClientOptions as ClientOpts,
  UniversalHelpClient,
} from '@stripe-internal/universal-help-conversations-client';
import {
  ClientResponse,
  EmailIssueClientUrls,
  ObservabilityConfig,
  RequestACallClientUrls,
  RequestAction,
} from '@stripe-internal/conversations-client';
import {InlineDataEventBus} from '@stripe-internal/inline-data';
import {TwilioClient} from '@stripe-internal/twilio-conversations-client';

import {Api} from './api/api';
import {AppConfig, AppProfile} from '../types';
import {SupportConversationsClient} from './SupportConversationsClient';

export function createClient({
  getConfig,
  getProfile,
  getApi,
  getObservability,
  inlineDataEventBus,
  requestAction,
}: {
  getConfig: () => AppConfig;
  getProfile: () => AppProfile;
  getApi: () => Api;
  getObservability?: () => ObservabilityConfig;
  inlineDataEventBus: InlineDataEventBus;
  requestAction: (action: RequestAction) => Promise<ClientResponse>;
}) {
  const {audience, entryPoint} = getConfig();
  const {merchant, featureFlags} = getProfile();

  const emailUrls: EmailIssueClientUrls = {
    emailIssueUrl: '/api/contact-form',
  };

  const racUrls: RequestACallClientUrls = {
    requestACallUrl: '/api/request-a-call',
    verificationCodesUrl: `/api/request-a-call/verification-code?redirectUrl=${document.referrer}`,
    channelsUrl: `/api/eligible-channels/${entryPoint}`,
  };

  const uhUrls = {
    conversationUrl: '/api/universal-help',
    ...emailUrls,
    ...racUrls,
  };

  const chatUrls = {
    tokenUrl: `/api/chat-token?audience=${audience}&entrypoint=${entryPoint}`,
    conversationUrl: `/api/start-chat?audience=${audience}&entrypoint=${entryPoint}`,
    ...emailUrls,
    ...racUrls,
  };

  const baseClientOpts: ClientOpts = {
    clientFetch: getApi().fetch,
    observability: getObservability?.(),
  };

  const chatClientOpts: ClientOpts = {
    ...baseClientOpts,
    urls: chatUrls,
  };

  const uhClientOpts: ClientOpts = {
    ...baseClientOpts,
    urls: uhUrls,
    userCountry: merchant?.country,
    enableUhPendingInput: featureFlags.enableUhPendingInput,
  };

  const uhClient = new UniversalHelpClient(uhClientOpts);

  const chatClient = new TwilioClient(chatClientOpts);

  const client = new SupportConversationsClient({
    ...uhClientOpts,
    getConfig,
    getProfile,
    getApi,
    getObservability,
    uhClient,
    chatClient,
    inlineDataEventBus,
    requestAction,
  });

  return {
    client,
    chatClient,
    uhClient,
  };
}
